import React, { useEffect, useRef, useState } from "react";
import { FormDataConsumer, SimpleFormIterator } from "react-admin";
import { ArrayInput } from "react-admin";
import { ICountryAppData } from "../../../common/redux/reducers/appDataReducer";
import "../styles/TwoInputsInRow.css";
import {
  SimpleFormIteratorAddButton,
  SimpleFormIteratorRemoveButton,
} from "./ProLogsSimpleFormIterator";
import { Void } from "../data-grid-fields/Void";
import { DrivingCountryCycleRuleSelector } from "./DrivingCountryCycleRuleSelector";

interface ICycleRuleSelectorProps {
  countries: ICountryAppData[];
  source: string;
  label?: string;
  record?: any;
  validate?: ((
    value: any,
    values: any
  ) =>
    | string
    | {
        message: string;
        args: any;
      }) & {
    isRequired: boolean;
  };
  disabled?: boolean;
}

export const CycleRulesSelector = (props: ICycleRuleSelectorProps) => {
  const {
    countries,
    source,
    validate,
    label,
    record,
    disabled,
    ...rest
  } = props;
  const [selectedCount, setSelectedCount] = useState<number>(
    record[source]?.length
  );
  const [addButtonVisible, setAddButtonVisible] = useState<boolean>(
    record[source]?.length !== countries.length
  );
  const selectedCountriesRef = useRef<{
    [key: string]: { id: number; remainingChoices: ICountryAppData[] };
  }>({});

  useEffect(() => {
    setAddButtonVisible(selectedCount !== countries.length);
  }, [selectedCount, countries.length]);

  const getRemainingCountries = (
    selectedCountries: Array<number>,
    currentSelectedCountryId: number
  ): Array<ICountryAppData> => {
    const initial = countries.map((c) => c.id!);
    const selected = new Set(
      selectedCountries.filter((x) => x !== currentSelectedCountryId)
    );
    const remaining = new Set(initial.filter((x) => !selected.has(x)));
    return countries.filter((x) => remaining.has(x.id!));
  };

  return (
    <ArrayInput source={source} validate={validate} label={label} {...rest}>
      <SimpleFormIterator
        disableAdd={disabled}
        disableRemove={disabled}
        disableReordering={disabled}
        addButton={
          addButtonVisible ? (
            <SimpleFormIteratorAddButton
              onClick={() => setSelectedCount(selectedCount + 1)}
            />
          ) : (
            <Void />
          )
        }
        removeButton={
          <SimpleFormIteratorRemoveButton
            onClick={() => setSelectedCount(selectedCount - 1)}
          />
        }>
        <FormDataConsumer>
          {(formDataProps) => {
            if (!formDataProps.getSource) {
              return null;
            }

            const { getSource, scopedFormData } = formDataProps;
            selectedCountriesRef.current = {
              ...selectedCountriesRef.current,
              [getSource("drivingCountryId")]: {
                id: scopedFormData?.drivingCountryId,
                remainingChoices: getRemainingCountries(
                  Object.values(selectedCountriesRef.current).map((c) => c.id),
                  scopedFormData?.drivingCountryId
                ),
              },
            };
            return (
              <DrivingCountryCycleRuleSelector
                getSource={getSource}
                selectedCountriesRef={selectedCountriesRef}
                countries={countries}
                scopedFormData={scopedFormData}
                disabled={disabled}
                {...rest}
              />
            );
          }}
        </FormDataConsumer>
      </SimpleFormIterator>
    </ArrayInput>
  );
};
